import React, { Suspense, lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { WorkstationShell } from './components/WorkstationShell';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Skeleton } from '../ui/primitives/Skeleton';

// Route-level code splitting (ADR-0018 & M1 Audit Correction)
const AuthPage = lazy(() =>
  import('./pages/AuthPage').then((m) => ({ default: m.AuthPage }))
);
const HomeDashboard = lazy(() =>
  import('./pages/HomeDashboard').then((m) => ({ default: m.HomeDashboard }))
);
const TrainingHub = lazy(() =>
  import('./pages/TrainingHub').then((m) => ({ default: m.TrainingHub }))
);
const ListeningExercise = lazy(() =>
  import('./pages/ListeningExercise').then((m) => ({ default: m.ListeningExercise }))
);
const DesktopSimulator = lazy(() =>
  import('./pages/DesktopSimulator').then((m) => ({ default: m.DesktopSimulator }))
);
const MobileSimulator = lazy(() =>
  import('./pages/MobileSimulator').then((m) => ({ default: m.MobileSimulator }))
);
const CallDebrief = lazy(() =>
  import('./pages/CallDebrief').then((m) => ({ default: m.CallDebrief }))
);
const PerformanceDashboard = lazy(() =>
  import('./pages/PerformanceDashboard').then((m) => ({ default: m.PerformanceDashboard }))
);
const Settings = lazy(() =>
  import('./pages/Settings').then((m) => ({ default: m.Settings }))
);
const DesignSystemGallery = lazy(() =>
  import('./pages/DesignSystemGallery').then((m) => ({ default: m.DesignSystemGallery }))
);
const ExercisePage = lazy(() =>
  import('./pages/ExercisePage').then((m) => ({ default: m.ExercisePage }))
);
const DiagnosticAssessmentPage = lazy(() =>
  import('./pages/DiagnosticAssessmentPage').then((m) => ({ default: m.DiagnosticAssessmentPage }))
);
const CommunicationLab = lazy(() =>
  import('./pages/CommunicationLab').then((m) => ({ default: m.CommunicationLab }))
);
const SupportSalesLab = lazy(() =>
  import('./pages/SupportSalesLab').then((m) => ({ default: m.SupportSalesLab }))
);

const PageFallback: React.FC = () => (
  <div
    className="space-y-6 max-w-6xl mx-auto p-4 animate-in fade-in-50 duration-150"
    role="status"
    aria-label="Loading workstation screen"
  >
    <div className="flex items-center justify-between">
      <Skeleton className="h-8 w-64 rounded-lg" />
      <Skeleton className="h-8 w-32 rounded-lg" />
    </div>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <Skeleton className="h-44 rounded-xl" />
      <Skeleton className="h-44 rounded-xl" />
      <Skeleton className="h-44 rounded-xl" />
    </div>
    <Skeleton className="h-80 rounded-xl" />
  </div>
);

const renderWithSuspense = (Component: React.ComponentType) => (
  <Suspense fallback={<PageFallback />}>
    <Component />
  </Suspense>
);

export const router = createBrowserRouter([
  // Public Entry: Clean Auth Experience (Sign In, Create Account, Forgot Password)
  {
    path: '/auth',
    element: renderWithSuspense(AuthPage),
  },

  // Protected Product Area: All protected routes require authenticated session
  {
    element: <ProtectedRoute />,
    children: [
      {
        path: '/',
        element: <WorkstationShell />,
        children: [
          {
            index: true,
            element: <Navigate to="/dashboard" replace />,
          },
          {
            path: 'dashboard',
            element: renderWithSuspense(HomeDashboard),
          },
          {
            path: 'training',
            element: renderWithSuspense(TrainingHub),
          },
          {
            path: 'training/communication',
            element: renderWithSuspense(CommunicationLab),
          },
          {
            path: 'training/support-sales',
            element: renderWithSuspense(SupportSalesLab),
          },
          {
            path: 'training/drill/:drillId',
            element: renderWithSuspense(ListeningExercise),
          },
          {
            path: 'training/exercise/:exerciseId',
            element: renderWithSuspense(ExercisePage),
          },
          {
            path: 'diagnostic',
            element: renderWithSuspense(DiagnosticAssessmentPage),
          },
          {
            path: 'simulator',
            element: <Navigate to="/simulator/call/SCEN-SIM-01" replace />,
          },
          {
            path: 'simulator/call',
            element: <Navigate to="/simulator/call/SCEN-SIM-01" replace />,
          },
          {
            path: 'simulator/call/:scenarioId',
            element: renderWithSuspense(DesktopSimulator),
          },
          {
            path: 'simulator/mobile/:scenarioId',
            element: renderWithSuspense(MobileSimulator),
          },
          {
            path: 'simulator/debrief',
            element: <Navigate to="/simulator/debrief/SCEN-SIM-01" replace />,
          },
          {
            path: 'simulator/debrief/:attemptId',
            element: renderWithSuspense(CallDebrief),
          },
          {
            path: 'performance',
            element: renderWithSuspense(PerformanceDashboard),
          },
          {
            path: 'settings',
            element: renderWithSuspense(Settings),
          },
          {
            path: 'dev/design-system',
            element: renderWithSuspense(DesignSystemGallery),
          },
        ],
      },
    ],
  },

  // Wildcard fallback: Unauthenticated will be intercepted by ProtectedRoute -> /auth
  {
    path: '*',
    element: <Navigate to="/dashboard" replace />,
  },
]);
